import { DvaReducer } from '@/models/shared';
import { AnyAction } from 'redux';
import { EffectsCommandMap, Subscription } from 'dva';
import { AVATAR_URL_PREFIX } from '@/constants';
import { ConnectState } from '@/models/connect';
import fMessage from '@/components/fMessage';
import { FUtil, FServiceAPI, FI18n } from '@freelog/tools-lib';
import { history } from '@@/core/history';
import { PolicyFullInfo_Type } from '@/type/contractTypes';
import {
  updateRewrite as updateRewriteHelper,
  fetchAndSortPolicies,
  addExhibitPolicy,
  updateExhibitPolicyStatus,
  updateExhibitOnlineStatus,
  updateExhibitBaseInfo,
  saveExhibitIntroduction,
  changeExhibitVersion,
} from '@/models/shared/exhibitHelpers';
// import FComponentsLib from '@freelog/components-lib';
import fPromiseModalConfirm from '@/components/fPromiseModalConfirm';
import dayjs from 'dayjs';
import { fSetHotspotTooltipVisible } from '@/components/FHotspotTooltip';
import type { IAttrsAndConfigsItem } from '@/components/FAttrsAndConfigs';

export interface ThemeDependencyPresentableItem {
  presentableId: string;
  title: string;
  cover: string;
  username: string;
  useAvatar: string;
  resourceTypes: string[];
  updateDate: string;
  version: string;
  optionRows: { label: string; value: string }[];
  /** 与展品「可选配置」同源结构；有则优先于 optionRows */
  optionalAttrsItems?: IAttrsAndConfigsItem[];
  /** dependencyTree / dependencyRewriteProperty 与接口路径中的 nid */
  dependencyNid: string;
  /** dependencyTree 节点 resourceId，供后续拉详情 */
  resourceId?: string;
}

/** 展品详情接口返回的 dependencyTree 节点 */
export interface PresentableDependencyTreeNode {
  nid?: string;
  resourceId: string;
  resourceName: string;
  resourceType?: string[];
  version: string;
  versionId?: string;
  versionRange?: string;
  fileSha1?: string;
  dependencies?: PresentableDependencyTreeNode[];
}

function splitResourceName(resourceName: string): { username: string; title: string } {
  const idx = resourceName.indexOf('/');
  if (idx <= 0) {
    return { username: '', title: resourceName };
  }
  return {
    username: resourceName.slice(0, idx),
    title: resourceName.slice(idx + 1) || resourceName,
  };
}

function mapDependencyTreeToThemeDependencyItems(
  tree: PresentableDependencyTreeNode[] | undefined | null,
): ThemeDependencyPresentableItem[] {
  if (!Array.isArray(tree) || tree.length === 0) {
    return [];
  }
  const pluginNodes = tree.filter(
    (node) => Array.isArray(node.resourceType) && node.resourceType.includes('插件'),
  );
  return pluginNodes.map((node, index) => {
    const dependencyNid =
      node.nid !== undefined && node.nid !== null && String(node.nid) !== ''
        ? String(node.nid)
        : node.resourceId;
    const id =
      (node.nid !== undefined && node.nid !== null && String(node.nid)) ||
      node.resourceId ||
      `dep_tree_${index}`;
    const { username, title } = splitResourceName(node.resourceName || '');
    return {
      presentableId: id,
      dependencyNid,
      resourceId: node.resourceId,
      title: title || node.resourceName || '',
      cover: '',
      username,
      useAvatar: '',
      resourceTypes: node.resourceType || [],
      updateDate: '',
      version: node.version || '',
      optionRows: [],
    };
  });
}

/** 展品详情 dependencyRewriteProperty 单项，与 dependencyTree 节点按 nid / resourceId 对应 */
export interface PresentableDependencyRewriteEntry {
  nid?: string;
  resourceId: string;
  resourceVersionId?: string;
  resourceSystemPropertyDescriptors: {
    insertMode?: number;
    key: string;
    defaultValue: number | string;
    name: string;
    remark: string;
    valueUnit: string;
    valueDisplay: string;
  }[];
  resourceCustomPropertyDescriptors: {
    candidateItems: string[];
    defaultValue: string;
    key: string;
    name: string;
    remark: string;
    type: 'readonlyText' | 'select' | 'editableText';
  }[];
  rewriteProperty: { key: string; name?: string; value: string; remark?: string }[];
  versionProperty: Record<string, string | number | boolean | null | undefined>;
}

function buildOptionalAttrsFromDependencyRewriteEntry(
  entry: PresentableDependencyRewriteEntry,
): IAttrsAndConfigsItem[] {
  const vp = entry.versionProperty || {};
  const rows: IAttrsAndConfigsItem[] = [];
  const readonlyBase = {
    item_Deletable: false,
    key_Editable: false,
    name_Editable: false,
    description_Editable: false,
    value_Editable: false as const,
  };

  const descriptorKeys = new Set(
    (entry.resourceCustomPropertyDescriptors || []).map((d) => d.key),
  );

  for (const rd of entry.resourceCustomPropertyDescriptors || []) {
    const fromVp = vp[rd.key];
    const valueStr =
      fromVp !== undefined && fromVp !== null && fromVp !== ''
        ? String(fromVp)
        : rd.defaultValue || '';

    if (rd.type === 'readonlyText') {
      rows.push({
        key: rd.key,
        name: rd.name,
        description: rd.remark || '',
        value: valueStr,
        configs: { ...readonlyBase },
      });
      continue;
    }

    if (rd.type === 'editableText' || rd.type === 'select') {
      rows.push({
        key: rd.key,
        name: rd.name,
        description: rd.remark || '',
        value: valueStr,
        configs: {
          item_Deletable: false,
          key_Editable: false,
          name_Editable: false,
          description_Editable: false,
          value_Editable:
            rd.type === 'select'
              ? {
                  configEnum: {
                    nullable: true,
                    defaultValue: rd.defaultValue || '',
                    options: rd.candidateItems || [],
                  },
                }
              : {
                  configText: {
                    nullable: true,
                    defaultValue: rd.defaultValue || '',
                    minLength: 1,
                    maxLength: 140,
                  },
                },
        },
      });
    }
  }

  for (const pr of entry.rewriteProperty || []) {
    if (descriptorKeys.has(pr.key)) {
      continue;
    }
    rows.push({
      key: pr.key,
      name: pr.name || pr.key,
      description: pr.remark || '',
      value: pr.value,
      configs: { ...readonlyBase },
    });
  }

  return rows;
}

function mergeThemeDependencyItemsWithRewrite(
  items: ThemeDependencyPresentableItem[],
  rewriteList: PresentableDependencyRewriteEntry[] | undefined | null,
): ThemeDependencyPresentableItem[] {
  if (!rewriteList?.length) {
    return items;
  }
  return items.map((item) => {
    const entry = rewriteList.find(
      (r) =>
        (item.resourceId && r.resourceId === item.resourceId) ||
        (r.nid !== undefined &&
          r.nid !== null &&
          String(r.nid) === String(item.presentableId)),
    );
    if (!entry) {
      return item;
    }
    return {
      ...item,
      dependencyNid:
        entry.nid !== undefined && entry.nid !== null && String(entry.nid) !== ''
          ? String(entry.nid)
          : item.dependencyNid,
      optionalAttrsItems: buildOptionalAttrsFromDependencyRewriteEntry(entry),
    };
  });
}

export interface ExhibitInfoPageModelState {
  pageLoading: boolean;

  exhibit_ID: string;
  exhibit_Name: string;
  exhibit_Title: string;
  exhibit_Online: boolean;
  exhibit_IsAuth: boolean;
  exhibit_AuthErrorText: string;
  exhibit_ResourceTypeCode: string;
  exhibit_BelongNode_ID: number;
  exhibit_BelongNode_Name: string;
  exhibit_BelongNode_Title: string;
  exhibit_BelongNode_ActiveThemeId: string;

  exhibit_Cover: string;
  exhibit_autoUpdate: boolean;

  exhibit_ResourceInfo: {
    id: string;
    cover: string;
    name: string;
    title: string;
    version: string;
    policy: string[];
    type: string[];
    status: 0 | 1 | 2 | 4;
    authProblem?: boolean;
    updateDate: string;
    username: string;
    useAvatar: string;
    isChoice: boolean;
  } | null;

  policy_List: PolicyFullInfo_Type[];

  graphShow: boolean;

  side_ExhibitTitle: string;
  side_ExhibitIntroduction: string;
  side_ExhibitTags: string[];
  side_AllVersions: string[];
  side_Version: string;
  side_SettingUnfold: boolean;
  side_RawProperties: {
    key: string;
    name: string;
    value: string;
    description: string;
  }[];
  side_BaseProperties: {
    key: string;
    name: string;
    value: string;
    description: string;
  }[];
  inheritOptions: {
    key: string;
    name: string;
    value: string;
    description: string;
    type: 'input' | 'select';
    options: string[];
    defaultValue: string;
  }[];
  customOptions: {
    key: string;
    name: string;
    value: string;
    description: string;
  }[];

  /** 主题展品依赖插件列表（展品详情 dependencyTree） */
  themeDependencyPresentables: ThemeDependencyPresentableItem[];
}

export interface ChangeAction extends AnyAction {
  type: 'change' | 'exhibitInfoPage/change';
  payload: Partial<ExhibitInfoPageModelState>;
}

export interface OnMountPageAction extends AnyAction {
  type: 'exhibitInfoPage/onMountPage';
  payload: {
    exhibitID: string;
  };
}

export interface OnUnmountPageAction extends AnyAction {
  type: 'exhibitInfoPage/onUnmountPage';
}

export interface FetchInfoAction extends AnyAction {
  type: 'fetchInfo' | 'exhibitInfoPage/fetchInfo';
}

export interface AddAPolicyAction extends AnyAction {
  type: 'exhibitInfoPage/addAPolicy';
  payload: {
    title: string;
    text: string;
  };
}

export interface UpdateAPolicyAction extends AnyAction {
  type: 'exhibitInfoPage/updateAPolicy';
  payload: {
    id: string;
    status: 0 | 1;
  };
}

export interface UpdateBaseInfoAction extends AnyAction {
  type: 'exhibitInfoPage/updateBaseInfo';
  payload: Partial<
    Pick<ExhibitInfoPageModelState, 'exhibit_Cover' | 'side_ExhibitTitle' | 'side_ExhibitTags'>
  >;
}

export interface UpdateStatusAction extends AnyAction {
  type: 'exhibitInfoPage/updateStatus';
  payload: 0 | 1;
}

export interface UpdateRewriteAction extends AnyAction {
  type: 'exhibitInfoPage/updateRewrite' | 'updateRewrite';
}

export interface ChangeVersionAction extends AnyAction {
  type: 'exhibitInfoPage/changeVersion';
  payload: string;
}

export interface OnSave_Side_ExhibitIntroduction_Action extends AnyAction {
  type: 'exhibitInfoPage/onSave_Side_ExhibitIntroduction';
  payload: {
    value: string;
  };
}

export interface OnSync_Options_Action extends AnyAction {
  type: 'exhibitInfoPage/onSync_Options';
  payload?: {
    successMessage?: string;
  };
}

export interface ExhibitInfoPageModelType {
  namespace: 'exhibitInfoPage';
  state: ExhibitInfoPageModelState;
  effects: {
    onMountPage: (action: OnMountPageAction, effects: EffectsCommandMap) => void;
    onUnmountPage: (action: OnUnmountPageAction, effects: EffectsCommandMap) => void;
    fetchInfo: (action: FetchInfoAction, effects: EffectsCommandMap) => void;
    addAPolicy: (action: AddAPolicyAction, effects: EffectsCommandMap) => void;
    updateAPolicy: (action: UpdateAPolicyAction, effects: EffectsCommandMap) => void;
    updateBaseInfo: (action: UpdateBaseInfoAction, effects: EffectsCommandMap) => void;
    updateStatus: (action: UpdateStatusAction, effects: EffectsCommandMap) => void;
    updateRewrite: (action: UpdateRewriteAction, effects: EffectsCommandMap) => void;
    changeVersion: (action: ChangeVersionAction, effects: EffectsCommandMap) => void;
    onSave_Side_ExhibitIntroduction: (
      action: OnSave_Side_ExhibitIntroduction_Action,
      effects: EffectsCommandMap,
    ) => void;
    onSync_Options: (action: OnSync_Options_Action, effects: EffectsCommandMap) => void;
  };
  reducers: {
    change: DvaReducer<ExhibitInfoPageModelState, ChangeAction>;
  };
  subscriptions: {
    setup: Subscription;
  };
}

const initStates: ExhibitInfoPageModelState = {
  pageLoading: true,

  exhibit_ID: '',
  exhibit_Name: '',
  exhibit_Title: '',
  exhibit_Online: false,
  exhibit_IsAuth: true,
  exhibit_AuthErrorText: '',
  exhibit_ResourceTypeCode: '',
  exhibit_BelongNode_ID: -1,
  exhibit_BelongNode_Name: '',
  exhibit_BelongNode_Title: '',
  exhibit_BelongNode_ActiveThemeId: '',
  exhibit_Cover: '',
  exhibit_autoUpdate: false,
  exhibit_ResourceInfo: null,
  policy_List: [],

  graphShow: true,

  side_ExhibitTitle: '',
  side_ExhibitIntroduction: '',
  side_ExhibitTags: [],
  side_AllVersions: [],
  side_Version: '',
  side_SettingUnfold: false,
  side_RawProperties: [],
  side_BaseProperties: [],
  inheritOptions: [],
  customOptions: [],
  themeDependencyPresentables: [],
};

const Model: ExhibitInfoPageModelType = {
  namespace: 'exhibitInfoPage',
  state: initStates,
  effects: {
    *onMountPage({ payload }: OnMountPageAction, { put, call }: EffectsCommandMap) {
      yield put<ChangeAction>({
        type: 'change',
        payload: {
          exhibit_ID: payload.exhibitID,
        },
      });

      yield put<FetchInfoAction>({
        type: 'fetchInfo',
      });
    },
    *onUnmountPage({}: OnUnmountPageAction, { put }: EffectsCommandMap) {
      yield put<ChangeAction>({
        type: 'change',
        payload: {
          ...initStates,
        },
      });
    },
    *fetchInfo({}: FetchInfoAction, { call, select, put }: EffectsCommandMap) {
      const { exhibitInfoPage }: ConnectState = yield select(
        ({ exhibitInfoPage }: ConnectState) => ({
          exhibitInfoPage,
        }),
      );

      const params: Parameters<typeof FServiceAPI.Exhibit.presentableDetails>[0] = {
        presentableId: exhibitInfoPage.exhibit_ID,
        isLoadCustomPropertyDescriptors: 1,
        isLoadPolicyInfo: 1,
        isTranslate: 1,
        isLoadResourceDetailInfo: 1,
        isLoadDependencyTree: 1,
      };

      yield put<ChangeAction>({
        type: 'change',
        payload: {
          exhibit_ID: '',
        },
      });
      const {
        data: data_PresentableDetails,
      }: {
        data: {
          nodeId: number;
          userId: number;
          presentableId: string;
          presentableName: string;
          resourceInfo: {
            resourceId:string;
            resourceName: string;
            resourceOwnerId: number;
            resourceType: string[];
            resourceTypeCode: string;
          };
          resourceCustomPropertyDescriptors: {
            candidateItems: string[];
            defaultValue: string;
            key: string;
            name: string;
            remark: string;
            type: 'readonlyText' | 'select' | 'editableText';
          }[];
          coverImages: string[];
          onlineStatus: number;
          policies: any[];
          presentableTitle: string;
          presentableIntro: string;
          resolveResources: any[];
          tags: string[];
          version: string;
          resourceSystemProperty: {
            [k: string]: string;
          };
          resourceSystemPropertyDescriptors: {
            defaultValue: number | string;
            key: string;
            name: string;
            remark: string;
            valueDisplay: string;
            valueUnit: string;
          }[];
          presentableRewriteProperty: {
            key: string;
            remark: string;
            value: string;
          }[];
          autoUpdateStatus: 0 | 1;
          dependencyTree?: PresentableDependencyTreeNode[];
          dependencyRewriteProperty?: PresentableDependencyRewriteEntry[];
        };
      } = yield call(FServiceAPI.Exhibit.presentableDetails, params);
      // console.log(data_PresentableDetails, 'data_PresentableDetails wioejfsd;jkfjk;jlk');
      if (
        !data_PresentableDetails ||
        data_PresentableDetails.userId !== FUtil.Tool.getUserIDByCookies()
      ) {
        history.replace(FUtil.LinkTo.exception403({}));
        return;
      }

      const params3: Parameters<typeof FServiceAPI.Node.details>[0] = {
        nodeId: data_PresentableDetails.nodeId,
      };

      const { data: data_NodeDetails } = yield call(FServiceAPI.Node.details, params3);
      // console.log(data3, 'data90j23rlkfjasdfa');

      if (data_NodeDetails?.status === -1) {
        history.replace(FUtil.LinkTo.nodeDeleted({ nodeID: data_PresentableDetails.nodeId }));
        return;
      }

      if ((data_NodeDetails.status & 4) === 4) {
        history.replace(FUtil.LinkTo.nodeFreeze({ nodeID: data_PresentableDetails.nodeId }));
        return;
      }

      const params2: Parameters<typeof FServiceAPI.Resource.info>[0] = {
        resourceIdOrName: data_PresentableDetails.resourceInfo.resourceId,
      };

      const {
        data: data_ResourceInfo,
      }: {
        data: {
          resourceId: string;
          coverImages: string[];
          resourceName: string;
          resourceTitle: string;
          resourceTypeCode: string;
          resourceType: string[];
          updateDate: string;
          status: 0 | 1 | 2 | 4;
          latestVersion: string;
          resourceVersions: {
            version: string;
          }[];
          policies: PolicyFullInfo_Type[];
          // updateDate: string;
          username: string;
          userId: number;
          operationType: 0 | 1 | 2 | 3 | 4 | 5;
        };
      } = yield call(FServiceAPI.Resource.info, params2);

      // 要禁用的键
      const disabledRewriteKeys = [
        ...data_PresentableDetails.resourceCustomPropertyDescriptors.map((i) => i.key),
      ];

      // 获取展品授权结果
      const params1: Parameters<typeof FServiceAPI.Exhibit.batchAuth>[0] = {
        nodeId: data_PresentableDetails.nodeId,
        authType: 3,
        presentableIds: data_PresentableDetails.presentableId,
      };
      const { data: data_ExhibitBatchAuthResults } = yield call(
        FServiceAPI.Exhibit.batchAuth,
        params1,
      );

      const policies: PolicyFullInfo_Type[] = [...data_PresentableDetails.policies];
      policies.reverse();

      policies.sort((a, b) => {
        return a.status === 1 && b.status === 0 ? -1 : 0;
      });


      yield put<ChangeAction>({
        type: 'change',
        payload: {
          pageLoading: false,
          exhibit_BelongNode_ID: data_PresentableDetails.nodeId,
          exhibit_BelongNode_Name: data_NodeDetails.nodeName,
          exhibit_BelongNode_Title: data_NodeDetails.nodeTitle,
          exhibit_BelongNode_ActiveThemeId: data_NodeDetails.nodeThemeId,
          exhibit_ID: data_PresentableDetails.presentableId,
          exhibit_Name: data_PresentableDetails.presentableName,
          exhibit_Title: data_PresentableDetails.presentableTitle,
          exhibit_Online: data_PresentableDetails.onlineStatus === 1,
          exhibit_IsAuth: data_ExhibitBatchAuthResults
            ? data_ExhibitBatchAuthResults[0].isAuth
            : true,
          exhibit_AuthErrorText: data_ExhibitBatchAuthResults
            ? data_ExhibitBatchAuthResults[0].error
            : '',
          exhibit_ResourceTypeCode: data_PresentableDetails.resourceInfo.resourceTypeCode,
          exhibit_Cover: data_PresentableDetails.coverImages[0] || '',
          exhibit_autoUpdate: data_PresentableDetails.autoUpdateStatus === 1,
          exhibit_ResourceInfo: {
            id: data_ResourceInfo.resourceId,
            cover: data_ResourceInfo.coverImages[0] || '',
            name: data_ResourceInfo.resourceName,
            title: data_ResourceInfo.resourceTitle,
            version: data_ResourceInfo.latestVersion,
            policy: data_ResourceInfo.policies
              .filter((l) => {
                return l.status === 1;
              })
              .map((l) => l.policyName),
            type: data_ResourceInfo.resourceType,
            status: data_ResourceInfo.status,
            authProblem: false,
            updateDate: dayjs(data_ResourceInfo.updateDate).format(FUtil.Predefined.momentDateFormat),
            username: data_ResourceInfo.username,
            useAvatar: `${AVATAR_URL_PREFIX}${data_ResourceInfo.userId}`,
            isChoice: data_ResourceInfo.operationType === 5,
          },
          policy_List: policies,

          side_ExhibitTitle: data_PresentableDetails.presentableTitle,
          side_ExhibitIntroduction: data_PresentableDetails.presentableIntro || '',
          side_ExhibitTags: data_PresentableDetails.tags,

          side_AllVersions: data_ResourceInfo.resourceVersions.map((d2: any) => d2.version),
          side_Version: data_PresentableDetails.version,
          side_RawProperties: data_PresentableDetails.resourceSystemPropertyDescriptors
            .filter((spd) => {
              return spd.defaultValue !== '';
            })
            .map((spd) => {
              return {
                key: spd.key,
                name: spd.name,
                value: spd.valueDisplay,
                description: spd.remark,
              };
            }),
          side_BaseProperties: data_PresentableDetails.resourceCustomPropertyDescriptors
            .filter((rd) => {
              return rd.type === 'readonlyText' && rd.defaultValue !== '';
            })
            .map((rd: any) => ({
              key: rd.key,
              name: rd.name,
              value: rd.defaultValue,
              description: rd.remark,
            })),
          inheritOptions: data_PresentableDetails.resourceCustomPropertyDescriptors
            .filter((rd) => {
              return rd.type === 'editableText' || rd.type === 'select';
            })
            .map<ExhibitInfoPageModelState['inheritOptions'][number]>((rd) => {
              const prp = data_PresentableDetails.presentableRewriteProperty.find(
                (pr) => pr.key === rd.key,
              );
              const value = prp ? prp.value : rd.defaultValue;
              return {
                key: rd?.key || '',
                name: rd?.name || '',
                value: value,
                description: rd.remark || '',
                type: rd.type === 'select' ? 'select' : 'input',
                options: rd.type === 'select' ? rd.candidateItems : [],
                defaultValue: rd.defaultValue || '',
              };
            }),
          customOptions: (data_PresentableDetails.presentableRewriteProperty as any[])
            .filter((pr: any) => !disabledRewriteKeys.includes(pr.key))
            .map<ExhibitInfoPageModelState['customOptions'][number]>((pr) => {
              return {
                key: pr.key,
                name: pr.name,
                value: pr.value,
                description: pr.remark,
              };
            }),
          themeDependencyPresentables: mergeThemeDependencyItemsWithRewrite(
            mapDependencyTreeToThemeDependencyItems(data_PresentableDetails.dependencyTree),
            data_PresentableDetails.dependencyRewriteProperty,
          ),
        },
      });
    },
    *addAPolicy({ payload }: AddAPolicyAction, { call, select, put }: EffectsCommandMap) {
      const { exhibitInfoPage }: ConnectState = yield select(
        ({ exhibitInfoPage }: ConnectState) => ({
          exhibitInfoPage,
        }),
      );

      const success: boolean = yield call(addExhibitPolicy, exhibitInfoPage.exhibit_ID, payload.title, payload.text);
      if (!success) return;

      fSetHotspotTooltipVisible('exhibitDetailPage.onlineSwitch', {
        value: true,
        effectiveImmediately: true,
        onlyNullish: true,
      });

      setTimeout(() => {
        fSetHotspotTooltipVisible('exhibitDetailPage.onlineSwitch', {
          value: false,
          effectiveImmediately: false,
          onlyNullish: false,
        });
      });

      if (
        !exhibitInfoPage.exhibit_Online &&
        !exhibitInfoPage.exhibit_ResourceInfo?.type.includes('主题')
      ) {
        const online: boolean = yield call(fPromiseModalConfirm, {
          title: '展品待上架',
          description: '将展品上架开放授权，为你带来更多收益',
          okText: '立即上架',
          cancelText: '暂不上架',
        });

        if (online) {
          yield call(updateExhibitOnlineStatus, exhibitInfoPage.exhibit_ID, 1);
          fSetHotspotTooltipVisible('exhibitDetailPage.onlineSwitch', {
            value: false,
            effectiveImmediately: true,
            onlyNullish: false,
          });
        }
      }

      const { policies, onlineStatus }: { policies: any[]; onlineStatus: number } = yield call(fetchAndSortPolicies, exhibitInfoPage.exhibit_ID);

      yield put<ChangeAction>({
        type: 'change',
        payload: {
          policy_List: policies,
          exhibit_Online: onlineStatus === 1,
        },
      });
    },
    *updateAPolicy({ payload }: UpdateAPolicyAction, { call, select, put }: EffectsCommandMap) {
      const { exhibitInfoPage }: ConnectState = yield select(
        ({ exhibitInfoPage }: ConnectState) => ({
          exhibitInfoPage,
        }),
      );
      yield call(updateExhibitPolicyStatus, exhibitInfoPage.exhibit_ID, payload.id, payload.status);

      const { policies }: { policies: any[] } = yield call(fetchAndSortPolicies, exhibitInfoPage.exhibit_ID);

      yield put<ChangeAction>({
        type: 'change',
        payload: {
          policy_List: policies,
        },
      });
    },
    *updateBaseInfo({ payload }: UpdateBaseInfoAction, { select, call, put }: EffectsCommandMap) {
      const { exhibitInfoPage }: ConnectState = yield select(
        ({ exhibitInfoPage }: ConnectState) => ({
          exhibitInfoPage,
        }),
      );

      const result: { success: boolean; msg?: string } = yield call(updateExhibitBaseInfo, exhibitInfoPage.exhibit_ID, {
        presentableTitle: payload.side_ExhibitTitle,
        tags: payload.side_ExhibitTags,
        // @ts-ignore
        presentableIntro: payload.side_ExhibitInputIntroduction || undefined,
        coverImages: payload.exhibit_Cover ? [payload.exhibit_Cover] : undefined,
      });
      if (!result.success) {
        fMessage(result.msg!, 'error');
        return;
      }
      yield put<ChangeAction>({
        type: 'change',
        payload,
      });
    },
    *updateStatus({ payload }: UpdateStatusAction, { call, select, put }: EffectsCommandMap) {
      const { exhibitInfoPage }: ConnectState = yield select(
        ({ exhibitInfoPage }: ConnectState) => ({
          exhibitInfoPage,
        }),
      );
      const success: boolean = yield call(updateExhibitOnlineStatus, exhibitInfoPage.exhibit_ID, payload);
      if (!success) {
        fMessage(
          exhibitInfoPage.exhibit_ResourceInfo?.type.includes('主题') ? '激活失败' : '上线失败',
          'error',
        );
        return;
      }
      yield put<FetchInfoAction>({
        type: 'fetchInfo',
      });
    },
    *updateRewrite({}: UpdateRewriteAction, { select, call, put }: EffectsCommandMap) {
      const { exhibitInfoPage }: ConnectState = yield select(
        ({ exhibitInfoPage }: ConnectState) => ({
          exhibitInfoPage,
        }),
      );
      yield call(updateRewriteHelper, {
        exhibit_ID: exhibitInfoPage.exhibit_ID,
        inheritOptions: exhibitInfoPage.inheritOptions,
        customOptions: exhibitInfoPage.customOptions,
      });
    },
    *changeVersion({ payload }: ChangeVersionAction, { call, put, select }: EffectsCommandMap) {
      const { exhibitInfoPage }: ConnectState = yield select(
        ({ exhibitInfoPage }: ConnectState) => ({
          exhibitInfoPage,
        }),
      );
      const result: { success: boolean; msg?: string } = yield call(changeExhibitVersion, exhibitInfoPage.exhibit_ID, payload);
      if (!result.success) {
        fMessage(result.msg!, 'error');
        return;
      }
      fMessage('切换成功', 'success');
      yield put<FetchInfoAction>({
        type: 'fetchInfo',
      });
    },
    *onSave_Side_ExhibitIntroduction(
      { payload }: OnSave_Side_ExhibitIntroduction_Action,
      { select, call, put }: EffectsCommandMap,
    ) {
      const { exhibitInfoPage }: ConnectState = yield select(
        ({ exhibitInfoPage }: ConnectState) => ({
          exhibitInfoPage,
        }),
      );
      const oldText: string = exhibitInfoPage.side_ExhibitIntroduction;
      yield put<ChangeAction>({
        type: 'change',
        payload: {
          side_ExhibitIntroduction: payload.value,
        },
      });

      const result: { success: boolean; msg?: string } = yield call(saveExhibitIntroduction, exhibitInfoPage.exhibit_ID, payload.value);
      if (!result.success) {
        fMessage(result.msg!, 'error');
        yield put<ChangeAction>({
          type: 'change',
          payload: {
            side_ExhibitIntroduction: oldText,
          },
        });
        return;
      }
    },
    *onSync_Options({ payload }: OnSync_Options_Action, { select, call, put }: EffectsCommandMap) {
      const { exhibitInfoPage }: ConnectState = yield select(
        ({ exhibitInfoPage }: ConnectState) => ({
          exhibitInfoPage,
        }),
      );

      const {
        data,
        errCode,
        ret,
        msg,
      }: {
        data: boolean;
        errCode: number;
        msg: string;
        ret: number;
      } = yield call(updateRewriteHelper, {
        exhibit_ID: exhibitInfoPage.exhibit_ID,
        inheritOptions: exhibitInfoPage.inheritOptions,
        customOptions: exhibitInfoPage.customOptions,
      });
      if (ret !== 0 || errCode !== 0 || !data) {
        return fMessage(msg, 'error');
      }
      if (payload?.successMessage) {
        fMessage(payload?.successMessage);
      }
    },
  },
  reducers: {
    change(state, { payload }) {
      return {
        ...state,
        ...payload,
      };
    },
  },
  subscriptions: {
    setup({}) {},
  },
};

export default Model;

export type { HandleRelationParams, HandleRelationResult, UpdateRewriteParams } from '@/models/shared/exhibitHelpers';
export { handleRelation, getAllContracts, updateRewrite } from '@/models/shared/exhibitHelpers';
